import React, { useRef, useState, useCallback } from "react";

interface Layer {
  /** Image URL */
  src: string;
  /** 0 = far / no movement, 1 = near / most movement */
  depth: number;
}

interface Props {
  /** Array of image layers ordered back-to-front */
  layers: Layer[];
}

/**
 * TiltParallax — pointer / gyro-driven layered depth effect.
 * Moving the cursor shifts each layer proportionally to its depth value,
 * giving a tactile 3-D parallax feel. Pointer-events are enabled on the
 * container so it can track mouse position.
 */
const TiltParallax: React.FC<Props> = ({ layers }) => {
  const ref = useRef<HTMLDivElement>(null);
  const [tilt, setTilt] = useState({ x: 0, y: 0 });

  const onMove = useCallback((e: React.MouseEvent) => {
    const el = ref.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const x = (e.clientX - rect.left) / rect.width - 0.5;
    const y = (e.clientY - rect.top) / rect.height - 0.5;
    setTilt({ x, y });
  }, []);

  const reset = useCallback(() => setTilt({ x: 0, y: 0 }), []);

  return (
    <div
      ref={ref}
      onMouseMove={onMove}
      onMouseLeave={reset}
      style={{
        position: "absolute",
        inset: 0,
        overflow: "hidden",
        pointerEvents: "auto",
      }}
    >
      {layers.map((l, i) => (
        <img
          key={i}
          src={l.src}
          alt=""
          style={{
            position: "absolute",
            inset: 0,
            width: "112%",
            height: "112%",
            objectFit: "cover",
            transform: `translate(${tilt.x * l.depth * -24}px, ${tilt.y * l.depth * -16}px) scale(1.03)`,
            transition: "transform 0.15s ease-out",
            zIndex: i,
            pointerEvents: "none",
          }}
        />
      ))}
    </div>
  );
};

export default TiltParallax;
